import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { prefersReducedMotion } from '../lib/gsap';
import { canWebGL } from './Journey';
import { StaticChip } from './StaticVisuals';

export const transit = { active: false };
const HOME_Y = 'sm-home-y';
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const Ctx = createContext(null);
export const useSceneNav = () => useContext(Ctx);

const T = (b, deg) => `translate(${b.x - 50}px, ${b.y - 50}px) scale(${b.s / 100}) rotateY(${deg}deg)`;
const center = (k = 1) => ({ x: window.innerWidth / 2, y: window.innerHeight / 2, s: Math.min(window.innerWidth * 0.62, window.innerHeight * 0.42, 340) * k });
const boxOf = (el, k = 0.35) => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2, s: Math.max(36, Math.min(r.width, r.height) * k) };
};
const sceneBox = () => {
  const el = document.querySelector('[data-scene-mode]');
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2, s: r.width * Number(el.dataset.chipRatio || 0.43) };
};
const waitFor = (fn, ms) => new Promise((res) => {
  const t0 = performance.now();
  const tick = () => { const v = fn(); if (v || performance.now() - t0 > ms) res(v); else requestAnimationFrame(tick); };
  tick();
});
const frame = () => new Promise((r) => requestAnimationFrame(() => r()));

export const SceneTransitionProvider = ({ children }) => {
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const navType = useNavigationType();
  const layer = useRef(null);
  const veil = useRef(null);
  const sweep = useRef(null);
  const chip = useRef(null);
  const busy = useRef(false);
  const prev = useRef(pathname);
  const fancy = useRef(null);
  if (fancy.current === null) fancy.current = !prefersReducedMotion() && canWebGL();

  const show = (on) => { layer.current.style.display = on ? 'block' : 'none'; };
  const anim = (el, frames, duration) => el.animate(frames, { duration, easing: EASE, fill: 'forwards' }).finished;
  const finish = () => {
    [layer.current, veil.current, sweep.current, chip.current].forEach((el) => el.getAnimations().forEach((a) => a.cancel()));
    veil.current.style.opacity = '0';
    show(false); transit.active = false; busy.current = false; window.dispatchEvent(new Event('sm:scene-play')); };

  const go = useCallback(async (to, src, opts = {}) => {
    if (busy.current || to === window.location.pathname) return;
    busy.current = true;
    try { await run(to, src, opts); } catch (e) { if (window.location.pathname !== to) navigate(to); } finally { finish(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const run = async (to, src, { back = false }) => {
    if (window.location.pathname === '/') sessionStorage.setItem(HOME_Y, String(window.scrollY));
    show(true);
    if (!fancy.current) {
      chip.current.style.opacity = '0';
      await anim(veil.current, [{ opacity: 0 }, { opacity: 1 }], 220);
      navigate(to, { state: back ? { restore: true } : null });
      await frame();
      await anim(veil.current, [{ opacity: 1 }, { opacity: 0 }], 280);
      return;
    }
    transit.active = !back;
    chip.current.style.opacity = '';
    const from = back ? (sceneBox() || center(0.5)) : (boxOf(src) || center(0.3));
    const mid = center();
    anim(veil.current, [{ opacity: 0 }, { opacity: 1 }], 420);
    anim(sweep.current, [{ transform: 'translateX(-120%)' }, { transform: 'translateX(260%)' }], 520);
    await anim(chip.current, [{ transform: T(from, back ? 0 : -40), opacity: back ? 1 : 0 }, { transform: T(mid, back ? -360 : 360), opacity: 1 }], 480);
    navigate(to, { state: back ? { restore: true } : null });
    if (back) {
      await frame();
      await Promise.all([
        anim(chip.current, [{ transform: T(mid, -360), opacity: 1 }, { transform: T(center(0.55), -400), opacity: 0 }], 420),
        anim(veil.current, [{ opacity: 1 }, { opacity: 0 }], 420),
      ]);
      return;
    }
    const found = await waitFor(sceneBox, 700);
    const dest = found && found.y - found.s / 2 < window.innerHeight * 0.85 ? found : center(0.8);
    await anim(chip.current, [{ transform: T(mid, 360), opacity: 1 }, { transform: T(dest, 360), opacity: 1 }], 380);
    window.dispatchEvent(new Event('sm:scene-play'));
    await Promise.all([
      anim(chip.current, [{ opacity: 1 }, { opacity: 0 }], 320),
      anim(veil.current, [{ opacity: 1 }, { opacity: 0 }], 320),
    ]);
  };

  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a || a.target === '_blank') return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/services/')) return;
      e.preventDefault();
      go(url.pathname, a);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [go]);

  useLayoutEffect(() => {
    const from = prev.current;
    prev.current = pathname;
    const saved = sessionStorage.getItem(HOME_Y);
    const restore = pathname === '/' && saved !== null && (navType === 'POP' || (state && state.restore));
    if (pathname === '/' && !restore) sessionStorage.removeItem(HOME_Y);
    if (!restore) { if (from !== pathname) window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); return; }
    sessionStorage.removeItem(HOME_Y);
    const y = Number(saved);
    window.scrollTo({ top: y, left: 0, behavior: 'instant' });
    requestAnimationFrame(() => window.scrollTo({ top: y, left: 0, behavior: 'instant' }));
    if (navType === 'POP' && from.startsWith('/services/') && !busy.current) {
      show(true);
      chip.current.style.opacity = fancy.current ? '' : '0';
      chip.current.style.transform = T(center(), 0);
      veil.current.style.opacity = '1';
      anim(chip.current, [{ transform: T(center(), 0), opacity: 1 }, { transform: T(center(0.55), -60), opacity: 0 }], 520);
      anim(veil.current, [{ opacity: 1 }, { opacity: 0 }], 560).then(finish, finish);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, navType]);

  return (
    <Ctx.Provider value={go}>
      {children}
      <div ref={layer} className="fixed inset-0 z-[90] overflow-hidden" style={{ display: 'none', perspective: '1200px' }} aria-hidden="true" data-testid="scene-transition">
        <div ref={veil} className="absolute inset-0 bg-[#080808]" style={{ opacity: 0 }} />
        <div ref={sweep} className="absolute inset-y-0 left-0 w-[45%] bg-gradient-to-r from-transparent via-[#C8AA73]/20 to-transparent" style={{ transform: 'translateX(-120%)' }} />
        <div ref={chip} className="absolute left-0 top-0 w-[100px] h-[100px] will-change-transform" style={{ opacity: 0 }} data-testid="scene-transition-chip">
          <StaticChip className="w-full" />
        </div>
      </div>
    </Ctx.Provider>
  );
};
